/**
 * 合同详情（延伸需求 3.3 / 3.5 / 3.6）
 *
 * 一张合同的完整档案：执行进度、明细、结算阶段、付款、变更、关联发票、
 * 审批留痕、履约评价、附件。
 *
 * 【为什么都在一个页面里】详情页要同时看到这些——拆成六个页面意味着
 * 六次跳转与六处"这个面板为什么是空的"。合同体量小，一次取回更可靠。
 * 数据由 `GET /contracts/:id` 一次返回（后端已把子表都聚合好）。
 */

import { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  DatePicker,
  Empty,
  Progress,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ContractChangeStatus,
  CONTRACT_CHANGE_STATUS_LABELS,
  ContractChangeType,
  CONTRACT_CHANGE_TYPE_LABELS,
  ContractSettlementStage,
  CONTRACT_SETTLEMENT_STAGE_LABELS,
  ContractStatus,
  CONTRACT_STATUS_LABELS,
  ContractType,
  CONTRACT_TYPE_LABELS,
  CounterpartyRole,
  COUNTERPARTY_ROLE_LABELS,
  ExpensePaymentMethod,
  EXPENSE_PAYMENT_METHOD_LABELS,
  PermissionCode,
  SettlementMethod,
  SETTLEMENT_METHOD_LABELS,
} from '@ilc/shared';
import { contractApi } from '@/api/endpoints';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { Money, moneyCellStyle } from '@/components/Money';
import { MaskedField } from '@/components/MaskedField';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { ContractAttachmentIntake } from '@/components/ContractAttachmentIntake';
import { AttachmentStatusTag } from '@/components/AttachmentStatusTag';
import { RiskTag } from '@/components/RiskTag';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';

type Detail = Awaited<ReturnType<typeof contractApi.detail>>;

export function ContractDetailPage(): React.ReactElement {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);

  const res = useResource<Detail>(() => contractApi.detail(id), { deps: [id], enabled: !!id });

  const canWrite = can(PermissionCode.CONTRACT_WRITE);
  const canPayment = can(PermissionCode.CONTRACT_PAYMENT_WRITE);
  const canEvaluate = can(PermissionCode.CONTRACT_EVALUATE);
  const canApproveChange = can(PermissionCode.CONTRACT_APPROVE);

  const [stageModal, setStageModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [changeModal, setChangeModal] = useState(false);
  const [evalModal, setEvalModal] = useState(false);
  const [confirmPayment, setConfirmPayment] = useState<{ id: string; no: string } | null>(null);
  const [applyChangeTarget, setApplyChangeTarget] = useState<{ id: string; no: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const [paymentForm] = Form.useForm();
  const [changeForm] = Form.useForm();
  const [evalForm] = Form.useForm();
  const [stageForm] = Form.useForm();
  /** 附件收件用的空表单实例：详情页没有编辑表单，而组件契约要求一个（见其 isNew 分支） */
  const [attachmentForm] = Form.useForm();

  const reload = useCallback(() => res.reload(), [res]);

  if (res.loading || res.error || !res.data) {
    return (
      <div className="ilc-page">
        <PageHeader title="合同详情" />
        <PageState
          loading={res.loading}
          error={res.error}
          empty={!res.loading && !res.error && !res.data}
          emptyText="合同不存在或不在你的可见范围内"
          onRetry={reload}
        >
          <div />
        </PageState>
      </div>
    );
  }

  const { contract: c } = res.data;
  const items = res.data.items ?? [];
  const stages = res.data.stages ?? [];
  const payments = res.data.payments ?? [];
  const changes = res.data.changes ?? [];
  const evaluations = res.data.evaluations ?? [];
  const invoices = res.data.invoices ?? [];
  const approvals = res.data.approvals ?? [];
  const alerts = res.data.alerts ?? [];
  const children = res.data.children ?? [];

  /** 执行进度：只用于画进度条，数值本身来自服务端聚合 */
  const invoiceRatio = c.amountCents > 0 ? c.invoicedCents / c.amountCents : 0;
  const payRatio = c.amountCents > 0 ? c.paidCents / c.amountCents : 0;

  const saveStage = async (): Promise<void> => {
    const v = await stageForm.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          contractApi.saveStages(id, [
            {
              stage: v.stage,
              name: v.name || undefined,
              ratio: v.ratio ?? null,
              amountCents: Math.round(Number(v.amountYuan ?? 0) * 100),
              dueDate: v.dueDate ? dayjs(v.dueDate).format('YYYY-MM-DD') : null,
              remark: v.remark || null,
            },
          ]),
        successMessage: '结算阶段已保存',
        offline: () => undefined,
        offlineMessage: '后端接口尚未就绪，本次保存仅在本地演示',
      });
      if (result.ok) {
        setStageModal(false);
        stageForm.resetFields();
        reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const savePayment = async (): Promise<void> => {
    const v = await paymentForm.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          contractApi.createPayment(id, {
            stage: v.stage ?? null,
            amountCents: Math.round(Number(v.amountYuan ?? 0) * 100),
            payDate: v.payDate ? dayjs(v.payDate).format('YYYY-MM-DD') : null,
            method: v.method ?? null,
            payeeName: v.payeeName || null,
            payeeAccount: v.payeeAccount || null,
            payeeBank: v.payeeBank || null,
            remark: v.remark || null,
          }),
        successMessage: '付款申请已登记',
        offline: () => undefined,
        offlineMessage: '后端接口尚未就绪，本次登记仅在本地演示',
      });
      if (result.ok) {
        setPaymentModal(false);
        paymentForm.resetFields();
        reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const saveChange = async (): Promise<void> => {
    const v = await changeForm.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          contractApi.createChange(id, {
            changeType: v.changeType,
            reason: v.reason,
            amountDeltaCents: Math.round(Number(v.deltaYuan ?? 0) * 100),
            changes: {
              amountCents: Math.round(Number(v.amountYuan) * 100),
              ...(v.endDate ? { endDate: dayjs(v.endDate).format('YYYY-MM-DD') } : {}),
            },
          }),
        successMessage: '变更已登记，审批通过后才生效',
        offline: () => undefined,
        offlineMessage: '后端接口尚未就绪，本次登记仅在本地演示',
      });
      if (result.ok) {
        setChangeModal(false);
        changeForm.resetFields();
        reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const saveEval = async (): Promise<void> => {
    const v = await evalForm.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          contractApi.createEvaluation(id, {
            period: v.period,
            qualityScore: v.qualityScore,
            priceScore: v.priceScore,
            deliveryScore: v.deliveryScore,
            serviceScore: v.serviceScore,
            comment: v.comment || null,
          }),
        successMessage: '履约评价已保存',
        offline: () => undefined,
        offlineMessage: '后端接口尚未就绪，本次评价仅在本地演示',
      });
      if (result.ok) {
        setEvalModal(false);
        evalForm.resetFields();
        reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const doConfirmPayment = async (reason: string): Promise<void> => {
    if (!confirmPayment) return;
    const result = await performAction({
      action: () => contractApi.confirmPayment(id, confirmPayment.id, { reason }),
      successMessage: '付款已确认',
      offline: () => undefined,
      offlineMessage: '后端接口尚未就绪，本次确认仅在本地演示',
    });
    if (result.ok) {
      // 超合同总额时后端会带回差额，如实提示而不是当成成功就完了
      const over = (result.data as { overContractCents?: number } | null)?.overContractCents ?? 0;
      if (over > 0) {
        feedback.warning(
          `付款已确认，但累计付款已超出合同总额 ¥${(over / 100).toFixed(2)}。` +
            '多半是补充协议尚未录入，请核对。',
        );
      }
      setConfirmPayment(null);
      reload();
    }
  };

  const doApplyChange = async (reason: string): Promise<void> => {
    if (!applyChangeTarget) return;
    const result = await performAction({
      action: () => contractApi.applyChange(id, applyChangeTarget.id, { note: reason }),
      successMessage: '变更已生效，合同金额已更新',
      offline: () => undefined,
      offlineMessage: '后端接口尚未就绪，本次应用仅在本地演示',
    });
    if (result.ok) {
      setApplyChangeTarget(null);
      reload();
    }
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title={
          <Space>
            <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/business/contracts')}>
              返回台账
            </Button>
            <span>
              {c.contractNo} {c.name}
            </span>
          </Space>
        }
        tags={
          <Space wrap>
            <Tag color="blue">{CONTRACT_TYPE_LABELS[c.contractType as ContractType] ?? c.contractType}</Tag>
            <Tag>{c.level}</Tag>
            <Tag color={c.status === ContractStatus.ACTIVE ? 'success' : 'processing'}>
              {CONTRACT_STATUS_LABELS[c.status as ContractStatus] ?? c.status}
            </Tag>
            {(c.openAlertCount ?? 0) > 0 && <Tag color="error">{c.openAlertCount} 条预警</Tag>}
          </Space>
        }
        description={
          <span>
            {c.parentContractNo && (
              <>
                上级合同：<span className="ilc-mono">{c.parentContractNo}</span>
              </>
            )}
            {c.counterpartyRole === CounterpartyRole.OWNER ? '甲方' : '乙方'}：{c.supplierName}
            {c.deptName && <>　经办部门：{c.deptName}</>}
            {c.handlerName && <>　经办人：{c.handlerName}</>}
          </span>
        }
        extra={
          <Space wrap>
            {canWrite && c.status === ContractStatus.ACTIVE && (
              <Button onClick={() => setStageModal(true)} icon={<PlusOutlined />}>
                维护结算阶段
              </Button>
            )}
            {canPayment && (
              <Button type="primary" onClick={() => setPaymentModal(true)} icon={<PlusOutlined />}>
                登记付款
              </Button>
            )}
            {canWrite && (
              <Button onClick={() => setChangeModal(true)}>变更 / 补充协议</Button>
            )}
            {canEvaluate && <Button onClick={() => setEvalModal(true)}>履约评价</Button>}
            <Button icon={<ReloadOutlined />} onClick={reload}>
              刷新
            </Button>
          </Space>
        }
      />

      {alerts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`有 ${alerts.length} 条未处理预警`}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {alerts.slice(0, 3).map((a) => (
                <li key={a.id}>{a.message}</li>
              ))}
            </ul>
          }
          action={
            <Button size="small" onClick={() => navigate('/business/contract-alerts')}>
              去处理
            </Button>
          }
        />
      )}

      {/* —— 执行进度 —— */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="合同金额" value={c.amountCents / 100} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="已开票 / 占比"
              value={c.invoicedCents / 100}
              precision={2}
              prefix="¥"
              suffix={`(${(invoiceRatio * 100).toFixed(0)}%)`}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="剩余可开票"
              value={c.remainingCents / 100}
              precision={2}
              prefix="¥"
              valueStyle={{ color: c.remainingCents === 0 && c.amountCents > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="已付款 / 占比"
              value={c.paidCents / 100}
              precision={2}
              prefix="¥"
              suffix={`(${(payRatio * 100).toFixed(0)}%)`}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: 'overview',
            label: '基本信息',
            children: (
              <Card size="small">
                <Descriptions bordered size="small" column={{ xs: 1, md: 2, xl: 3 }}>
                  <Descriptions.Item label="合同编号">
                    <span className="ilc-mono">{c.contractNo}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="合同名称">{c.name}</Descriptions.Item>
                  <Descriptions.Item label="合同类型">
                    {CONTRACT_TYPE_LABELS[c.contractType as ContractType] ?? c.contractType}
                  </Descriptions.Item>
                  <Descriptions.Item label="层级">{c.level}（由类型推导）</Descriptions.Item>
                  <Descriptions.Item label="对方身份">
                    {COUNTERPARTY_ROLE_LABELS[c.counterpartyRole as CounterpartyRole] ?? c.counterpartyRole}
                  </Descriptions.Item>
                  <Descriptions.Item label="对方单位">{c.supplierName}</Descriptions.Item>
                  <Descriptions.Item label="对方税号">
                    <MaskedField value={c.supplierTaxNo} kind="taxNo" permission={PermissionCode.CONTRACT_READ} />
                  </Descriptions.Item>
                  <Descriptions.Item label="含税金额">
                    <Money cents={c.amountCents} strong />
                  </Descriptions.Item>
                  <Descriptions.Item label="不含税金额">
                    <Money cents={c.amountExclTaxCents} />
                  </Descriptions.Item>
                  <Descriptions.Item label="税额">
                    <Money cents={c.taxCents} />
                  </Descriptions.Item>
                  <Descriptions.Item label="结算方式">
                    {c.settlementMethod
                      ? (SETTLEMENT_METHOD_LABELS[c.settlementMethod as SettlementMethod] ?? c.settlementMethod)
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="结算阶段（比对用）">{c.settlementStage ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="签订日期">{c.signDate ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="工期起">{c.startDate ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="工期止">{c.endDate ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="质保期届满">{c.warrantyEndDate ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="约定品类">
                    {(c.categories ?? []).length > 0
                      ? (c.categories ?? []).map((x) => <Tag key={x}>{x}</Tag>)
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="审批通过于">
                    {c.approvedAt ? dayjs(c.approvedAt).format('YYYY-MM-DD HH:mm') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="归档于">
                    {c.archivedAt ? dayjs(c.archivedAt).format('YYYY-MM-DD HH:mm') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="备注" span={3}>
                    {c.remark ?? '-'}
                  </Descriptions.Item>
                </Descriptions>
                {children.length > 0 && (
                  <>
                    <h4 style={{ marginTop: 16 }}>下级合同（{children.length}）</h4>
                    <Table
                      size="small"
                      rowKey="id"
                      pagination={false}
                      dataSource={children}
                      columns={[
                        { title: '编号', dataIndex: 'contractNo', render: (v: string) => <span className="ilc-mono">{v}</span> },
                        { title: '名称', dataIndex: 'name' },
                        { title: '类型', dataIndex: 'contractType', render: (v: string) => CONTRACT_TYPE_LABELS[v as ContractType] ?? v },
                        { title: '对方单位', dataIndex: 'counterpartyName' },
                        {
                          title: '金额',
                          dataIndex: 'amountCents',
                          align: 'right' as const,
                          onCell: () => ({ style: moneyCellStyle }),
                          render: (v: number) => <Money cents={v} />,
                        },
                        { title: '状态', dataIndex: 'status', render: (v: string) => CONTRACT_STATUS_LABELS[v as ContractStatus] ?? v },
                      ]}
                    />
                  </>
                )}
              </Card>
            ),
          },
          {
            key: 'items',
            label: `明细（${items.length}）`,
            children: (
              <Card size="small">
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={items}
                  locale={{ emptyText: <Empty description="还没有合同明细。约定品类也可以直接填在基本信息里。" /> }}
                  columns={[
                    { title: '品类', dataIndex: 'category' },
                    {
                      title: '金额',
                      dataIndex: 'amountCents',
                      align: 'right' as const,
                      onCell: () => ({ style: moneyCellStyle }),
                      render: (v: number) => <Money cents={v} />,
                    },
                    {
                      title: '税率',
                      dataIndex: 'taxRate',
                      width: 90,
                      // 未约定时渲染「-」而不是 0%：0% 是免税的意思，与"没填"不是一回事
                      render: (v: number | null) => (v === null ? '-' : `${(Number(v) * 100).toFixed(2)}%`),
                    },
                    {
                      title: '所属阶段',
                      dataIndex: 'stage',
                      width: 110,
                      render: (v: string | null) =>
                        v ? (CONTRACT_SETTLEMENT_STAGE_LABELS[v as ContractSettlementStage] ?? v) : '-',
                    },
                    { title: '备注', dataIndex: 'remark', render: (v: string | null) => v ?? '-' },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'stages',
            label: `结算阶段（${stages.length}）`,
            children: (
              <Card size="small">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="阶段上限：约定了比例就按「比例 × 合同金额」算（合同变更后自动跟着变），只约定金额就用金额本身"
                />
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={stages}
                  locale={{ emptyText: <Empty description="还没有结算阶段计划。点右上角「维护结算阶段」新增。" /> }}
                  columns={[
                    { title: '阶段', dataIndex: 'name', width: 120 },
                    {
                      title: '约定比例',
                      dataIndex: 'ratio',
                      width: 100,
                      render: (v: number | null) => (v === null ? '-' : `${(Number(v) * 100).toFixed(1)}%`),
                    },
                    {
                      title: '约定金额',
                      dataIndex: 'amountCents',
                      align: 'right' as const,
                      onCell: () => ({ style: moneyCellStyle }),
                      render: (v: number) => <Money cents={v} />,
                    },
                    {
                      title: '阶段上限',
                      dataIndex: 'limitCents',
                      align: 'right' as const,
                      onCell: () => ({ style: moneyCellStyle }),
                      render: (v: number) => <Money cents={v} />,
                    },
                    {
                      title: '已付',
                      dataIndex: 'paidCents',
                      align: 'right' as const,
                      onCell: () => ({ style: moneyCellStyle }),
                      render: (v: number) => <Money cents={v} />,
                    },
                    {
                      title: '进度',
                      key: 'progress',
                      width: 160,
                      render: (_: unknown, r) => {
                        const pct = r.limitCents > 0 ? Math.min(100, (r.paidCents / r.limitCents) * 100) : 0;
                        return (
                          <Space size={4}>
                            <Progress percent={Math.round(pct)} size="small" style={{ width: 90 }} />
                            {/* 超比例是风险项，标红而不是只改颜色 */}
                            {r.overLimit && <Tag color="error">超上限</Tag>}
                          </Space>
                        );
                      },
                    },
                    { title: '到期日', dataIndex: 'dueDate', width: 110, render: (v: string | null) => v ?? '-' },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'payments',
            label: `付款（${payments.length}）`,
            children: (
              <Card size="small">
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={payments}
                  locale={{ emptyText: <Empty description="还没有付款记录。点右上角「登记付款」新增。" /> }}
                  columns={[
                    { title: '付款单号', dataIndex: 'paymentNo', render: (v: string) => <span className="ilc-mono">{v}</span> },
                    {
                      title: '阶段',
                      dataIndex: 'stage',
                      width: 100,
                      render: (v: string | null) => (v ? (CONTRACT_SETTLEMENT_STAGE_LABELS[v as ContractSettlementStage] ?? v) : '-'),
                    },
                    {
                      title: '金额',
                      dataIndex: 'amountCents',
                      align: 'right' as const,
                      onCell: () => ({ style: moneyCellStyle }),
                      render: (v: number) => <Money cents={v} strong />,
                    },
                    {
                      title: '方式',
                      dataIndex: 'method',
                      width: 100,
                      render: (v: string | null) =>
                        v ? (EXPENSE_PAYMENT_METHOD_LABELS[v as ExpensePaymentMethod] ?? v) : '-',
                    },
                    { title: '付款日期', dataIndex: 'payDate', width: 110, render: (v: string | null) => v ?? '-' },
                    { title: '收款方', dataIndex: 'payeeName', ellipsis: true, render: (v: string | null) => v ?? '-' },
                    {
                      title: '收款账号',
                      dataIndex: 'payeeAccount',
                      width: 170,
                      // 银行账号是敏感字段（需求 4 字段权限），按权限脱敏
                      render: (v: string | null) => (
                        <MaskedField value={v} kind="bankAccount" permission={PermissionCode.CONTRACT_PAYMENT_WRITE} />
                      ),
                    },
                    {
                      title: '状态',
                      dataIndex: 'status',
                      width: 96,
                      render: (v: string) => {
                        const map: Record<string, { color: string; label: string }> = {
                          APPLIED: { color: 'default', label: '已申请' },
                          APPROVED: { color: 'processing', label: '已批准' },
                          PAID: { color: 'success', label: '已付款' },
                          VOID: { color: 'error', label: '已作废' },
                        };
                        const m = map[v] ?? { color: 'default', label: v };
                        return <Tag color={m.color}>{m.label}</Tag>;
                      },
                    },
                    {
                      title: '操作',
                      key: 'act',
                      width: 100,
                      render: (_: unknown, r) =>
                        canPayment && r.status !== 'PAID' && r.status !== 'VOID' ? (
                          <Button size="small" type="link" onClick={() => setConfirmPayment({ id: r.id, no: r.paymentNo })}>
                            确认付款
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'changes',
            label: `变更（${changes.length}）`,
            children: (
              <Card size="small">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="合同金额不能在编辑里直接改。变更走独立流程：登记 → 审批 → 应用（应用后才改合同主表）"
                  description="只记增量回答不了“改之前是什么”，所以每条变更都留了前后快照。"
                />
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={changes}
                  locale={{ emptyText: <Empty description="还没有变更或补充协议。" /> }}
                  columns={[
                    { title: '编号', dataIndex: 'changeNo', render: (v: string) => <span className="ilc-mono">{v}</span> },
                    {
                      title: '类型',
                      dataIndex: 'changeType',
                      width: 110,
                      render: (v: string) => CONTRACT_CHANGE_TYPE_LABELS[v as ContractChangeType] ?? v,
                    },
                    { title: '原因', dataIndex: 'reason', ellipsis: true },
                    {
                      title: '金额增量',
                      dataIndex: 'amountDeltaCents',
                      align: 'right' as const,
                      onCell: () => ({ style: moneyCellStyle }),
                      render: (v: number) => (
                        // 减项要看得见负号，不要让财务自己算
                        <span style={{ color: v < 0 ? '#cf1322' : undefined }}>
                          <Money cents={v} colorNegative />
                        </span>
                      ),
                    },
                    {
                      title: '变更前金额',
                      key: 'before',
                      align: 'right' as const,
                      onCell: () => ({ style: moneyCellStyle }),
                      render: (_: unknown, r) => <Money cents={Number(r.beforeSnapshot?.['amountCents'] ?? 0)} />,
                    },
                    {
                      title: '状态',
                      dataIndex: 'status',
                      width: 96,
                      render: (v: string) => (
                        <Tag color={v === ContractChangeStatus.APPLIED ? 'success' : 'default'}>
                          {CONTRACT_CHANGE_STATUS_LABELS[v as ContractChangeStatus] ?? v}
                        </Tag>
                      ),
                    },
                    { title: '申请人', dataIndex: 'createdByName', width: 100, render: (v: string | null) => v ?? '-' },
                    {
                      title: '操作',
                      key: 'act',
                      width: 100,
                      render: (_: unknown, r) =>
                        canApproveChange && r.status !== ContractChangeStatus.APPLIED && r.status !== ContractChangeStatus.REJECTED ? (
                          <Button
                            size="small"
                            type="link"
                            onClick={() => setApplyChangeTarget({ id: r.id, no: r.changeNo })}
                          >
                            应用变更
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'invoices',
            label: `关联发票（${invoices.length}）`,
            children: (
              <Card size="small">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="这里包含全部来源的发票——报销上传的票若挂了这张合同，它就是这张合同下真实开出的一张票"
                />
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={invoices}
                  locale={{ emptyText: <Empty description="还没有关联发票。" /> }}
                  columns={[
                    { title: '业务单号', dataIndex: 'bizNo', render: (v: string) => <span className="ilc-mono">{v}</span> },
                    { title: '发票号码', dataIndex: 'invoiceNo', width: 180, render: (v: string | null) => <span className="ilc-mono">{v ?? '-'}</span> },
                    { title: '开票日期', dataIndex: 'invoiceDate', width: 110, render: (v: string | null) => v ?? '-' },
                    { title: '销方', dataIndex: 'sellerName', ellipsis: true, render: (v: string | null) => v ?? '-' },
                    {
                      title: '价税合计',
                      dataIndex: 'totalCents',
                      align: 'right' as const,
                      onCell: () => ({ style: moneyCellStyle }),
                      render: (v: number | null) => <Money cents={v} />,
                    },
                    {
                      title: '风险',
                      dataIndex: 'riskLevel',
                      width: 90,
                      render: (v: string | null) => (v ? <RiskTag level={v as never} /> : '-'),
                    },
                    {
                      title: '来源',
                      dataIndex: 'bizSource',
                      width: 90,
                      render: (v: string) => (v === 'EXPENSE' ? <Tag color="purple">费用报销</Tag> : <Tag>发票业务</Tag>),
                    },
                    {
                      title: '操作',
                      key: 'act',
                      width: 80,
                      render: (_: unknown, r) => (
                        <a href={`/business/invoices/${r.id}`}>查看</a>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'approvals',
            label: `审批留痕（${approvals.length}）`,
            children: (
              <Card size="small">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="审批记录读自流程引擎（flow_task），不另存一份"
                  description="另建一张审批表就是两份留痕，等它们不一致时无从判断哪份是真的。"
                />
                <Table
                  size="small"
                  rowKey={(r) => `${r.instanceId}-${r.nodeKey}-${r.createdAt}`}
                  pagination={false}
                  dataSource={approvals}
                  locale={{ emptyText: <Empty description="尚未提交审批。" /> }}
                  columns={[
                    {
                      title: '业务',
                      dataIndex: 'bizType',
                      width: 110,
                      render: (v: string) => (v === 'CONTRACT_CHANGE' ? '合同变更' : '合同'),
                    },
                    { title: '节点', dataIndex: 'nodeName', width: 130 },
                    { title: '处理角色', dataIndex: 'assigneeRoleName', width: 120, render: (v: string | null) => v ?? '-' },
                    { title: '处理人', dataIndex: 'assigneeUserName', width: 100, render: (v: string | null) => v ?? '-' },
                    {
                      title: '动作',
                      dataIndex: 'action',
                      width: 100,
                      render: (v: string | null) =>
                        v ? <Tag>{v}</Tag> : <Tag color="processing">待处理</Tag>,
                    },
                    { title: '意见', dataIndex: 'comment', ellipsis: true, render: (v: string | null) => v ?? '-' },
                    {
                      title: '时间',
                      dataIndex: 'createdAt',
                      width: 150,
                      render: (v: string, r) =>
                        dayjs(r.handledAt ?? v).format('YYYY-MM-DD HH:mm'),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'evaluations',
            label: `履约评价（${evaluations.length}）`,
            children: (
              <Card size="small">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="总分与等级由服务端按四项加权算出（质量 35% / 交付 30% / 服务 20% / 价格 15%）"
                  description="不在前端各算一份：它们要参与排序与统计，两处算迟早出现同一个合同在不同页面上分数不同。"
                />
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={evaluations}
                  locale={{ emptyText: <Empty description="还没有履约评价。" /> }}
                  columns={[
                    { title: '期间', dataIndex: 'period', width: 100 },
                    { title: '质量', dataIndex: 'qualityScore', width: 70 },
                    { title: '价格', dataIndex: 'priceScore', width: 70 },
                    { title: '交付', dataIndex: 'deliveryScore', width: 70 },
                    { title: '服务', dataIndex: 'serviceScore', width: 70 },
                    {
                      title: '总分',
                      dataIndex: 'totalScore',
                      width: 80,
                      render: (v: number) => <strong>{v}</strong>,
                    },
                    {
                      title: '等级',
                      dataIndex: 'level',
                      width: 90,
                      render: (v: string) => {
                        const color = v === 'A' ? 'success' : v === 'B' ? 'blue' : v === 'C' ? 'warning' : 'error';
                        return <Tag color={color}>{v}</Tag>;
                      },
                    },
                    { title: '评价人', dataIndex: 'evaluatedByName', width: 100, render: (v: string | null) => v ?? '-' },
                    { title: '评语', dataIndex: 'comment', ellipsis: true, render: (v: string | null) => v ?? '-' },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'attachments',
            label: `附件（${res.data.attachments?.length ?? 0}）`,
            children: (
              <Card
                size="small"
                title="合同电子版"
                extra={
                  canWrite ? (
                    /*
                     * 往**已有合同**补传电子版。
                     *
                     * 【为什么这里也要有】登记时可以先不传文件（有人是先拿到盖章件再录系统，
                     * 顺序反过来很常见）。没有这个入口，那些合同就只能回「法务工作台」
                     * 或者干脆重录一遍。
                     *
                     * 【与新建时的一条关键差别】上传后**只展示识别结果，不改写已保存的字段**——
                     * 这个表单里是这份合同的现有数据，被识别结果盖掉不是"预填"而是"篡改"。
                     * 要采用得在面板上逐项点。这条规则写在组件里，两处共用。
                     */
                    <ContractAttachmentIntake
                      form={attachmentForm}
                      contractId={id}
                      showList={false}
                      onChanged={() => res.reload()}
                    />
                  ) : null
                }
              >
                {!res.data.attachments || res.data.attachments.length === 0 ? (
                  <Empty
                    description={
                      canWrite
                        ? '还没有上传合同电子版。点右上角上传后会自动提取工程名称、金额、税号、约定品类等，供你核对。'
                        : '还没有上传合同电子版'
                    }
                  />
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={res.data.attachments}
                    columns={[
                      { title: '文件名', dataIndex: 'fileName', ellipsis: true },
                      { title: '类型', dataIndex: 'fileKind', width: 80 },
                      {
                        title: '解析状态',
                        width: 260,
                        render: (_: unknown, r) => (
                          <AttachmentStatusTag
                            status={r.extractStatus}
                            impl={r.extractImpl}
                            mode={r.extractMode}
                            message={r.extractMessage}
                            missing={r.extractMissing}
                          />
                        ),
                      },
                      {
                        title: '表格',
                        width: 80,
                        render: (_: unknown, r) => (r.tableCount > 0 ? `${r.tableCount} 个` : '-'),
                      },
                      {
                        title: '上传于',
                        dataIndex: 'createdAt',
                        width: 150,
                        render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
                      },
                    ]}
                  />
                )}
              </Card>
            ),
          },
        ]}
      />

      {/* —— 维护结算阶段 —— */}
      <Modal
        open={stageModal}
        title="维护结算阶段"
        onCancel={() => setStageModal(false)}
        onOk={() => void saveStage()}
        confirmLoading={saving}
        destroyOnHidden
        okText="保存"
        width={620}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="保存会**整体替换**该合同的结算阶段"
          description="前端提交的是完整表单，「删掉一行」与「没动那一行」在增量语义下无法区分。所以按全量提交。"
        />
        <Form key="stage" form={stageForm} layout="vertical" preserve={false}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="stage" label="阶段" rules={[{ required: true, message: '请选择阶段' }]}>
                <Select
                  options={Object.entries(CONTRACT_SETTLEMENT_STAGE_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="显示名" extra="留空用阶段默认名">
                <Input placeholder="如：首期预付款" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="ratio" label="约定比例" extra="0-1 之间，如 0.3 表示 30%。留空表示只约定金额">
                <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.05} precision={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amountYuan" label="约定金额（元）" rules={[{ required: true, message: '请填写金额' }]}>
                <InputNumber style={{ width: '100%' }} precision={2} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="dueDate" label="到期日">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="remark" label="备注">
                <Input maxLength={500} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* —— 登记付款 —— */}
      <Modal
        open={paymentModal}
        title="登记付款"
        onCancel={() => setPaymentModal(false)}
        onOk={() => void savePayment()}
        confirmLoading={saving}
        destroyOnHidden
        okText="登记"
        width={680}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`剩余应付 ¥${(c.payableCents / 100).toFixed(2)}（合同金额 − 已付款）`}
          description="登记只是「申请」，钱还没出去。要实际付款请在这条记录上点「确认付款」——那一步不可逆，会写审计。"
        />
        <Form key="payment" form={paymentForm} layout="vertical" preserve={false}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="stage" label="所属结算阶段">
                <Select
                  allowClear
                  placeholder="选填"
                  options={Object.entries(CONTRACT_SETTLEMENT_STAGE_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amountYuan" label="付款金额（元）" rules={[{ required: true, message: '请填写金额' }]}>
                <InputNumber style={{ width: '100%' }} precision={2} min={0.01} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="method" label="付款方式">
                <Select
                  allowClear
                  options={Object.entries(EXPENSE_PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payDate" label="付款日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="payeeName" label="收款方" initialValue={c.supplierName}>
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payeeBank" label="开户行">
                <Input maxLength={200} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item name="payeeAccount" label="收款账号" extra="敏感字段，界面上按权限脱敏展示">
                <Input className="ilc-mono" maxLength={64} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* —— 变更 / 补充协议 —— */}
      <Modal
        open={changeModal}
        title="变更 / 补充协议"
        onCancel={() => setChangeModal(false)}
        onOk={() => void saveChange()}
        confirmLoading={saving}
        destroyOnHidden
        okText="登记变更"
        width={680}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`当前合同金额 ¥${(c.amountCents / 100).toFixed(2)}`}
          description="登记后需要经过审批；审批通过后在变更列表里点「应用变更」才会改到合同上。"
        />
        <Form key="change" form={changeForm} layout="vertical" preserve={false} initialValues={{ amountYuan: c.amountCents / 100, changeType: ContractChangeType.CHANGE }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="changeType" label="类型" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(CONTRACT_CHANGE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="deltaYuan" label="金额增量（元）" extra="减项填负数">
                <InputNumber style={{ width: '100%' }} precision={2} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amountYuan" label="变更后合同金额（元）" rules={[{ required: true, message: '请填写变更后金额' }]}>
                <InputNumber style={{ width: '100%' }} precision={2} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endDate" label="变更后工期止">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="reason" label="变更原因" rules={[{ required: true, message: '请填写变更原因' }]}>
            <Input.TextArea rows={3} maxLength={1000} placeholder="如：增加工程量，追加 100 万元" />
          </Form.Item>
        </Form>
      </Modal>

      {/* —— 履约评价 —— */}
      <Modal
        open={evalModal}
        title="履约评价"
        onCancel={() => setEvalModal(false)}
        onOk={() => void saveEval()}
        confirmLoading={saving}
        destroyOnHidden
        okText="保存"
        width={620}
      >
        <Form key="eval" form={evalForm} layout="vertical" preserve={false} initialValues={{ period: 'FINAL', qualityScore: 80, priceScore: 80, deliveryScore: 80, serviceScore: 80 }}>
          <Form.Item name="period" label="评价期间" rules={[{ required: true }]} extra="如 2026Q3 / FINAL">
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="qualityScore" label="质量">
                <InputNumber style={{ width: '100%' }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="deliveryScore" label="交付">
                <InputNumber style={{ width: '100%' }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="serviceScore" label="服务">
                <InputNumber style={{ width: '100%' }} min={0} max={100} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="priceScore" label="价格">
                <InputNumber style={{ width: '100%' }} min={0} max={100} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="comment" label="评语">
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>

      {/* —— 确认付款（不可逆） —— */}
      <ConfirmDanger
        open={confirmPayment !== null}
        title={`确认付款：${confirmPayment?.no ?? ''}`}
        description={
          <span>
            确认后表示<strong>钱已经付出去</strong>，这一步不可逆。
            <br />
            累计付款若超出合同总额，系统会提示差额并要求你留下说明——多半是补充协议还没录入。
          </span>
        }
        level="high"
        reasonLabel="付款说明（必填，将写入操作日志）"
        minReasonLength={4}
        confirmText="确认已付款"
        onConfirm={(reason) => void doConfirmPayment(reason)}
        onCancel={() => setConfirmPayment(null)}
      />

      {/* —— 应用变更（改合同主表） —— */}
      <ConfirmDanger
        open={applyChangeTarget !== null}
        title={`应用变更：${applyChangeTarget?.no ?? ''}`}
        description="应用后变更内容会写入合同主表，剩余额度与执行进度随之更新。已应用的变更不能重复应用。"
        level="normal"
        reasonLabel="应用说明（选填，将写入操作日志）"
        minReasonLength={0}
        confirmText="确认应用"
        onConfirm={(reason) => void doApplyChange(reason)}
        onCancel={() => setApplyChangeTarget(null)}
      />
    </div>
  );
}

export default ContractDetailPage;
